import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Share, X, Plus, RefreshCw } from 'lucide-react'
import { Button } from './ui/button'
import Modal from './ui/Modal'
import { cn } from '../lib/utils'

const DISMISS_KEY = 'pwa-install-dismissed-at'
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000 // re-show after 7 days

function isStandalone() {
  return (
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true)
  )
}

function isIOS() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY))
    if (!ts) return false
    return Date.now() - ts < DISMISS_WINDOW_MS
  } catch {
    return false
  }
}

export default function InstallPrompt() {
  const { t } = useTranslation()
  // Seed from the pre-React listener (public/index.html) so we don't miss
  // a beforeinstallprompt that fired before this component mounted. The
  // window event listener below covers the late-fire case for completeness.
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => (typeof window !== 'undefined' ? window.__deferredInstallPrompt : null) || null
  )
  const [dismissed, setDismissed] = useState(() => recentlyDismissed())
  const [installed, setInstalled] = useState(() => isStandalone())

  useEffect(() => {
    const onAvailable = () => {
      setDeferredPrompt(window.__deferredInstallPrompt || null)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferredPrompt(null)
    }
    // Late-fire fallback: if the event somehow fires after this component
    // mounts (e.g. the pre-React listener didn't run for some reason),
    // still capture it here.
    const onBeforeInstall = (e) => {
      e.preventDefault()
      window.__deferredInstallPrompt = e
      setDeferredPrompt(e)
    }

    window.addEventListener('app:install-available', onAvailable)
    window.addEventListener('app:installed', onInstalled)
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('app:install-available', onAvailable)
      window.removeEventListener('app:installed', onInstalled)
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  if (installed || dismissed) return null

  const handleInstall = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setInstalled(true)
    }
    // Clear the window cache too so a future event isn't shadowed by the
    // already-consumed one (the prompt can only be triggered once).
    if (typeof window !== 'undefined') {
      window.__deferredInstallPrompt = null
    }
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      /* ignore quota errors */
    }
    setDismissed(true)
  }

  // Chrome / Edge / Android — native install event captured
  if (deferredPrompt) {
    return (
      <div className="relative mb-4 rounded-lg border border-app-primary-200 dark:border-app-primary-900 bg-app-primary-50 dark:bg-app-primary-950/30 p-4 flex items-start gap-3">
        <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/40 p-2 flex-shrink-0">
          <Download className="w-5 h-5 text-app-primary-600 dark:text-app-primary-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-foreground">
            {t('pwa.install.title')}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('pwa.install.body')}
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
            >
              <Download className="w-4 h-4 me-1.5" />
              {t('pwa.install.button')}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              {t('pwa.install.dismiss')}
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label={t('pwa.install.dismiss')}
          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  // iOS Safari — no install event, show static instructions
  if (isIOS()) {
    return (
      <div className="relative mb-4 rounded-lg border border-app-primary-200 dark:border-app-primary-900 bg-app-primary-50 dark:bg-app-primary-950/30 p-4 flex items-start gap-3">
        <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/40 p-2 flex-shrink-0">
          <Download className="w-5 h-5 text-app-primary-600 dark:text-app-primary-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-foreground">
            {t('pwa.install.iosTitle')}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {t('pwa.install.iosStep1')}{' '}
            <Share className="w-3.5 h-3.5 inline-block mx-0.5 -mt-0.5 text-blue-500" />{' '}
            {t('pwa.install.iosStep2')}{' '}
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-background border border-border text-[11px] font-medium">
              <Plus className="w-3 h-3" />
              {t('pwa.install.iosAddToHome')}
            </span>
          </p>
        </div>
        <button
          onClick={handleDismiss}
          aria-label={t('pwa.install.dismiss')}
          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return null
}

export function UpdateToast() {
  const { t } = useTranslation()
  const [waitingRegistration, setWaitingRegistration] = useState(null)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    const onUpdate = (e) => {
      setWaitingRegistration(e.detail)
    }
    window.addEventListener('pwa:update-available', onUpdate)
    return () => window.removeEventListener('pwa:update-available', onUpdate)
  }, [])

  // Visible on every route now. The original home-only gate was a
  // defence against post-update reloads landing on a chunk-mismatch
  // white page from a deep route — that failure mode was fixed by
  // shipping absolute asset paths in PR #341. Restricting to home
  // meant users deep in the app never saw the prompt, so deploys
  // didn't land until the user happened to navigate home.
  if (!waitingRegistration) return null

  const handleReload = () => {
    if (updating) return
    setUpdating(true)

    if (waitingRegistration.waiting) {
      waitingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }

    // Plain reload — kept intentionally simple. On iOS standalone PWAs,
    // anything other than reload() (e.g. location.href = ...) can kick
    // the user out into Safari.
    let done = false
    const reload = () => {
      if (done) return
      done = true
      window.location.reload()
    }
    navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true })
    // Fallback in case controllerchange doesn't fire (e.g. there was no
    // prior controller). 3s leaves the SW enough time to activate.
    setTimeout(reload, 3000)
  }

  // Centered, REQUIRED modal — no dismiss / scrim / ESC paths. A
  // tap-by-mistake used to leave the user stuck with no way to surface
  // the prompt again until they happened to reload. Forcing the update
  // is safe for a single-user app where you always want the latest
  // build live; the 3s timeout fallback in handleReload keeps even a
  // hung SW activation from stranding the page.
  return (
    <Modal
      open
      closeOnScrim={false}
      closeOnEsc={false}
      ariaLabel={t('pwa.update.title')}
    >
      <div className="flex items-start gap-3" dir="auto">
        <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/40 p-2 flex-shrink-0">
          <RefreshCw className={cn('w-5 h-5 text-app-primary-600 dark:text-app-primary-400', updating && 'animate-spin')} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-foreground" dir="auto">{t('pwa.update.title')}</p>
          <p className="text-sm text-muted-foreground mt-1" dir="auto">{t('pwa.update.subtitle')}</p>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button
          onClick={handleReload}
          disabled={updating}
          className="w-full sm:w-auto bg-app-primary-500 hover:bg-app-primary-600 text-white"
        >
          {updating ? t('pwa.update.updating') : t('pwa.update.button')}
        </Button>
      </div>
    </Modal>
  )
}
